import { useEffect, useState, lazy, Suspense } from "react"
import LandingPage from "@/pages/LandingPage"
import { AuthProvider } from "@/contexts/AuthContext"
import { LocalizationProvider } from "@/contexts/LocalizationContext"
import { ThemeProvider, useTheme } from "@/contexts/ThemeContext"
import { ProtectedRoute } from "@/components/ProtectedRoute"
import { PublicRoute } from "@/components/PublicRoute"

// Lazy load all pages except LandingPage (needed immediately)
const AccessibilityPage = lazy(() => import("@/pages/AccessibilityPage"))
const CookiesPage = lazy(() => import("@/pages/CookiesPage"))
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"))
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"))
const PricingPage = lazy(() => import("@/pages/PricingPage"))
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"))
const SignInPage = lazy(() => import("@/pages/SignInPage"))
const SignUpPage = lazy(() => import("@/pages/SignUpPage"))
const TermsPage = lazy(() => import("@/pages/TermsPage"))
const DashboardPage = lazy(() => import("@/pages/DashboardPage"))
const InitialAssessmentPage = lazy(() => import("@/pages/InitialAssessmentPage"))
const DailyLogPage = lazy(() => import("@/pages/DailyLogPage"))
const AICompanionPage = lazy(() => import("@/pages/AICompanionPage"))
const UpgradePage = lazy(() => import("@/pages/UpgradePage"))
const Health = lazy(() => import("@/pages/Health"))
const HealthArticle = lazy(() => import("@/pages/HealthArticle"))

const routeComponents = {
  "/": LandingPage,
  "/fr": LandingPage,
  "/es": LandingPage,
  "/de": LandingPage,
  "/it": LandingPage,
  "/pt": LandingPage,
  "/nl": LandingPage,
  "/pl": LandingPage,
  "/terms": TermsPage,
  "/privacy": PrivacyPage,
  "/cookies": CookiesPage,
  "/accessibility": AccessibilityPage,
  "/signup": SignUpPage,
  "/signin": SignInPage,
  "/forgot-password": ForgotPasswordPage,
  "/reset-password": ResetPasswordPage,
  "/dashboard": DashboardPage,
  "/assessment": InitialAssessmentPage,
  "/daily-log": DailyLogPage,
  "/ai-companion": AICompanionPage,
  "/pricing": PricingPage,
  "/upgrade": UpgradePage,
  "/health": Health,
}

type RouteKey = keyof typeof routeComponents

function normalizePath(pathname: string): string {
  if (!pathname) return "/"
  const base = pathname.split("?")[0]?.split("#")[0] ?? "/"
  if (base === "" || base === "/") return "/"
  return base.endsWith("/") ? base.slice(0, -1) || "/" : base
}

function NotFoundPage() {
  useEffect(() => {
    document.title = "Page not found · Diagnoza.care"
  }, [])

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-br from-white via-emerald-50/30 to-white dark:from-slate-900 dark:via-emerald-950/10 dark:to-slate-900 px-6 text-center">
      <div className="max-w-2xl space-y-8">
        {/* Logo */}
        <div className="flex justify-center">
          <img
            src="/web-app-manifest-192x192.webp"
            alt="Diagnoza.care"
            className="h-24 w-24 object-contain opacity-80"
          />
        </div>

        {/* 404 Badge */}
        <div className="flex justify-center">
          <div className="rounded-2xl bg-gradient-to-r from-emerald-500/10 to-emerald-600/10 px-6 py-2 border border-emerald-200 dark:border-emerald-800">
            <p className="text-sm font-bold tracking-wider text-emerald-600 dark:text-emerald-400">ERROR 404</p>
          </div>
        </div>

        {/* Title & Description */}
        <div className="space-y-4">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white">
            Lost in the Healthcare Journey?
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-lg mx-auto">
            This page doesn't exist, but your health data is safe with us. Let's get you back on track.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
          <a
            href="/"
            className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 px-8 py-4 text-base font-semibold text-white shadow-lg transition-all hover:shadow-xl"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            Back to Home
          </a>
          <a
            href="/dashboard"
            className="inline-flex items-center gap-2 rounded-2xl border-2 border-emerald-200 dark:border-emerald-800 hover:bg-emerald-50 dark:hover:bg-emerald-950/20 px-8 py-4 text-base font-semibold text-emerald-700 dark:text-emerald-400 transition-all"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Go to Dashboard
          </a>
        </div>

        {/* Quick Links */}
        <div className="pt-8 border-t border-slate-200 dark:border-slate-800">
          <p className="text-sm text-slate-500 dark:text-slate-500 mb-4">Quick Links</p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <a href="/signup" className="text-emerald-600 dark:text-emerald-400 hover:underline">Sign Up</a>
            <span className="text-slate-300 dark:text-slate-700">•</span>
            <a href="/signin" className="text-emerald-600 dark:text-emerald-400 hover:underline">Sign In</a>
            <span className="text-slate-300 dark:text-slate-700">•</span>
            <a href="/privacy" className="text-emerald-600 dark:text-emerald-400 hover:underline">Privacy</a>
          </div>
        </div>
      </div>
    </div>
  )
}

function ThemeTransitionOverlay() {
  const { isTransitioning } = useTheme()

  return (
    <div className={`theme-transition-overlay ${isTransitioning ? 'active' : ''}`} />
  )
}

// Loading fallback component
function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-white via-emerald-50/30 to-white dark:from-slate-900 dark:via-emerald-950/10 dark:to-slate-900">
      <div className="flex flex-col items-center gap-4">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-emerald-200 border-t-emerald-600"></div>
        <p className="text-sm text-slate-600 dark:text-slate-400">Loading...</p>
      </div>
    </div>
  )
}

function AppContent() {
  const [path, setPath] = useState(() => normalizePath(window.location.pathname))

  // Scroll to top when path changes
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [path])

  useEffect(() => {
    const handlePopState = () => {
      setPath(normalizePath(window.location.pathname))
    }

    const handleClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return
      }

      const target = event.target
      if (!(target instanceof Element)) return

      const anchor = target.closest("a")
      if (!anchor) return

      const href = anchor.getAttribute("href")
      if (!href || href.startsWith("mailto:") || href.startsWith("tel:")) return
      if (anchor.target && anchor.target !== "") return
      if (anchor.hasAttribute("download")) return
      if (href.startsWith("#") || anchor.hash) return
      if (anchor.origin !== window.location.origin) return

      const nextPath = normalizePath(anchor.pathname)
      // Allow dynamic routes like /health/[slug]
      const isDynamicHealthRoute = nextPath.startsWith("/health/")
      if (!nextPath || (!isDynamicHealthRoute && !(nextPath in routeComponents))) return

      event.preventDefault()
      if (nextPath === path) return
      window.history.pushState({}, "", nextPath)
      setPath(nextPath)
    }

    window.addEventListener("popstate", handlePopState)
    document.addEventListener("click", handleClick)

    return () => {
      window.removeEventListener("popstate", handlePopState)
      document.removeEventListener("click", handleClick)
    }
  }, [path])

  // Check for dynamic routes first
  if (path.startsWith("/health/")) {
    const pathParts = path.replace("/health/", "").split("/")

    // Check if it's a language-specific route: /health/[lang]/[slug]
    const supportedLanguages = ['en', 'fr', 'es', 'de', 'it', 'pt', 'nl', 'pl']
    if (pathParts.length === 2 && supportedLanguages.includes(pathParts[0])) {
      const [language, slug] = pathParts
      return (
        <Suspense fallback={<PageLoader />}>
          <HealthArticle slug={slug} initialLanguage={language} />
        </Suspense>
      )
    }

    // Legacy route without language: /health/slug (defaults to user's language)
    const slug = pathParts[0]
    if (slug) {
      return (
        <Suspense fallback={<PageLoader />}>
          <HealthArticle slug={slug} />
        </Suspense>
      )
    }
  }

  const PageComponent = routeComponents[path as RouteKey]
  if (!PageComponent) {
    return <NotFoundPage />
  }

  // Landing page routes with language detection
  const landingLanguages = ['en', 'fr', 'es', 'de', 'it', 'pt', 'nl', 'pl']
  const isLandingPage = path === '/' || landingLanguages.some(lang => path === `/${lang}`)
  if (isLandingPage && PageComponent === LandingPage) {
    // Only pass initialLanguage if URL has explicit language path
    // For '/', let LandingPage read from localStorage
    const language = path !== '/' ? path.substring(1) : undefined
    return (
      <Suspense fallback={<PageLoader />}>
        <LandingPage initialLanguage={language} />
      </Suspense>
    )
  }

  // Protected routes (require authentication)
  if (path === "/dashboard" || path === "/assessment" || path === "/daily-log" || path === "/ai-companion") {
    return (
      <Suspense fallback={<PageLoader />}>
        <ProtectedRoute>
          <PageComponent />
        </ProtectedRoute>
      </Suspense>
    )
  }

  // Public routes (redirect to dashboard if authenticated)
  // Note: /signup handles its own success screen, so it's not wrapped
  if (path === "/signin" || path === "/forgot-password") {
    return (
      <Suspense fallback={<PageLoader />}>
        <PublicRoute>
          <PageComponent />
        </PublicRoute>
      </Suspense>
    )
  }

  // Regular pages (no auth check)
  return (
    <Suspense fallback={<PageLoader />}>
      <PageComponent />
    </Suspense>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <LocalizationProvider>
          <ThemeTransitionOverlay />
          <AppContent />
        </LocalizationProvider>
      </ThemeProvider>
    </AuthProvider>
  )
}
